import React from "react";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { CATEGORIES, BUNDLE_ITEMS } from "../data/products";
import ProductCard from "./ProductCard";
import Reveal from "./Reveal";

const BundleBanner = () => (
  <Reveal>
    <div
      data-testid="bundle-banner"
      className="relative overflow-hidden border border-[#2a2820] bg-[#0D0D0B] p-8 md:p-10 mb-20"
    >
      <div className="flex flex-col lg:flex-row lg:items-center gap-8 lg:gap-12">
        <div className="lg:w-1/3">
          <div className="text-[11px] tracking-[0.25em] uppercase font-bold text-[#E6DFD4] mb-3">
            Every Order Includes
          </div>
          <h3 className="font-display text-3xl md:text-4xl uppercase leading-none text-[#F5F5F5]">
            A Complete<br />Protocol Kit
          </h3>
        </div>
        <div className="lg:w-2/3 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
          {BUNDLE_ITEMS.map((item, i) => (
            <div key={i} className="flex items-center gap-3 border-b border-[#1c1c1c] pb-4">
              <Icons.Check size={16} className="text-[#E6DFD4] shrink-0" />
              <span className="text-sm text-[#C9C4BB]">{item}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  </Reveal>
);

export const Products = () => {
  return (
    <section id="products" data-testid="products-section" className="relative py-24 md:py-32 bg-[#050505]">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <Reveal>
          <div className="mb-14 max-w-3xl">
            <div className="text-[11px] tracking-[0.3em] uppercase font-bold text-[#737373] mb-4">
              The Arsenal
            </div>
            <h2 className="font-display text-5xl md:text-7xl uppercase leading-[0.9] text-[#F5F5F5]">
              Pharmaceutical-Grade Peptides
            </h2>
            <p className="mt-5 text-[#A3A3A3] text-base md:text-lg max-w-xl">
              Sixteen research compounds across five disciplines. Chosen for purity, dosed for results.
            </p>
            <div data-testid="products-disclaimer" className="mt-6 inline-flex items-start gap-2.5 border border-[#3a382f] bg-[#0f0f0d] px-4 py-3 max-w-xl">
              <Icons.AlertTriangle size={15} className="text-[#E6DFD4] mt-0.5 shrink-0" />
              <p className="text-[11px] tracking-[0.1em] uppercase text-[#C9C4BB] leading-snug">
                For laboratory & research purposes only. Not for human consumption, personal use, or veterinary use.
              </p>
            </div>
          </div>
        </Reveal>

        <BundleBanner />

        <div className="space-y-24">
          {CATEGORIES.map((cat) => {
            const Icon = Icons[cat.icon] || Icons.Circle;
            return (
              <div key={cat.id} data-testid={`category-${cat.id}`}>
                <Reveal>
                  <div className="flex items-center gap-4 mb-10 border-b border-[#1c1c1c] pb-6">
                    <div className="flex h-12 w-12 items-center justify-center border border-[#2c2c2c] bg-[#0D0D0D]">
                      <Icon size={20} className="text-[#E6DFD4]" />
                    </div>
                    <div>
                      <h3 className="font-display text-3xl md:text-4xl uppercase leading-none text-[#F5F5F5]">
                        {cat.label}
                      </h3>
                      <span className="text-[11px] tracking-[0.2em] uppercase text-[#737373]">
                        {cat.products.length} {cat.products.length === 1 ? "Product" : "Products"}
                      </span>
                    </div>
                  </div>
                </Reveal>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {cat.products.map((p, i) => (
                    <ProductCard key={p.name} product={p} vial={cat.vial} index={i} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Products;
